import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Loader, Text} from '@gravity-ui/uikit';
import {Crop, ReactCrop} from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import styles from './AvatarUploadModal.module.scss';
import {useAvatarUpload, EntityType, PreparedAvatarData} from '../../lib';
import {useParams} from 'react-router';
import {ActionModal} from '../../../../shared';

interface AvatarUploadModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  file: File | null;
  previewUrl: string | null;
  onUploadSuccess: () => void;
  entityId?: string;
  entityType?: EntityType;
  isCreating?: boolean;
  onPrepareAvatarData?: (data: PreparedAvatarData, previewUrl: string) => void;
}

export const AvatarUploadModal = ({
  open,
  setOpen,
  file,
  previewUrl: initialPreviewUrl,
  onUploadSuccess,
  entityId: propEntityId,
  entityType = EntityType.USER,
  isCreating = false,
  onPrepareAvatarData,
}: AvatarUploadModalProps) => {
  const [crop, setCrop] = useState<Crop | null>(null);
  const [completedCrop, setCompletedCrop] = useState<Crop | null>(null);
  const [resizeWidth, setResizeWidth] = useState<number | null>(null);
  const [resizeHeight, setResizeHeight] = useState<number | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [originalWidth, setOriginalWidth] = useState<number | null>(null);
  const [originalHeight, setOriginalHeight] = useState<number | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(initialPreviewUrl); // Local state for preview
  const params = useParams();

  const entityId = propEntityId || (params.id as string);
  const {prepareUpload, uploadAvatar, prepareAvatarData, isPreparing, isUploading, error: uploadError} = useAvatarUpload(
    isCreating ? undefined : entityId,
    entityType
  );

  // Сброс состояния при закрытии модального окна или смене файла
  useEffect(() => {
    if (!open) {
      setCrop(null);
      setCompletedCrop(null);
      setPreviewUrl(null);
      setOriginalWidth(null);
      setOriginalHeight(null);
      imgRef.current = null;
    } else {
      setPreviewUrl(initialPreviewUrl);
    }
  }, [open, initialPreviewUrl]);

  useEffect(() => {
    if (open && file && !isCreating) {
      prepareUpload(file);
    }
  }, [open, file, prepareUpload, isCreating]);

  useEffect(() => {
    if (open && file && !previewUrl) {
      const reader = new FileReader();
      reader.onloadend = () => setPreviewUrl(reader.result as string);
      reader.readAsDataURL(file);
    }
  }, [open, file, previewUrl]);

  // Обработчик загрузки изображения в ReactCrop
  const onImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    imgRef.current = e.currentTarget;
    const {naturalWidth: width, naturalHeight: height} = e.currentTarget; // Оригинальные размеры
    setOriginalWidth(width);
    setOriginalHeight(height);

    const displayWidth = e.currentTarget.width;
    const displayHeight = e.currentTarget.height;

    const initialCropSize = Math.min(300, displayWidth, displayHeight);

    const initialCrop: Crop = {
      unit: 'px',
      width: initialCropSize,
      height: initialCropSize,
      x: (displayWidth - initialCropSize) / 2,
      y: (displayHeight - initialCropSize) / 2,
    };

    setCrop(initialCrop);
    // Сразу устанавливаем completedCrop, чтобы кнопка загрузки была активна
    setCompletedCrop(initialCrop);
  }, []);

  // Обновляем resizeWidth/Height при изменении completedCrop
  useEffect(() => {
    if (completedCrop && imgRef.current && originalWidth && originalHeight) {
      // Рассчитываем масштаб относительно оригинального изображения
      const scaleX = originalWidth / imgRef.current.width;
      const scaleY = originalHeight / imgRef.current.height;

      setResizeWidth(Math.round(completedCrop.width * scaleX));
      setResizeHeight(Math.round(completedCrop.height * scaleY));
    } else {
      setResizeWidth(null);
      setResizeHeight(null);
    }
  }, [completedCrop, originalWidth, originalHeight]);

  // Функция для получения превью из canvas
  const getCanvasPreview = (): string | null => {
    if (!canvasRef.current) return null;
    return canvasRef.current.toDataURL('image/jpeg', 0.95);
  };

  // Обработчик нажатия кнопки "Загрузить"
  const handleUpload = async () => {
    if (completedCrop && imgRef.current && canvasRef.current && resizeWidth && resizeHeight) {
      if (isCreating && onPrepareAvatarData) {
        // Режим создания нового объекта - готовим данные для формы
        const avatarData = await prepareAvatarData(
          completedCrop,
          resizeWidth,
          resizeHeight,
          imgRef as React.RefObject<HTMLImageElement>,
          canvasRef as React.RefObject<HTMLCanvasElement>,
        );
        
        if (avatarData) {
          // Получаем превью изображения из canvas для отображения в аватаре
          const previewUrl = getCanvasPreview();
          if (previewUrl) {
            onPrepareAvatarData(avatarData, previewUrl);
          } else {
            onPrepareAvatarData(avatarData, '');
          }
          onUploadSuccess();
        }
      } else {
        // Режим обновления существующего объекта - загружаем сразу
        await uploadAvatar(
          completedCrop,
          resizeWidth,
          resizeHeight,
          imgRef as React.RefObject<HTMLImageElement>,
          canvasRef as React.RefObject<HTMLCanvasElement>,
        );

        if (!uploadError) {
          onUploadSuccess(); // Вызываем колбэк успешной загрузки (закроет модалку и обновит данные)
        } else {
          const errorMsg = uploadError || 'Не удалось загрузить аватар.';
          console.error('Upload error:', errorMsg);
        }
      }
    } else {
      console.error('Upload conditions not met:', {
        completedCrop,
        imgRef: imgRef.current,
        canvasRef: canvasRef.current,
        resizeWidth,
        resizeHeight,
      });
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  const isLoading = isPreparing || isUploading;

  return (
    <ActionModal
      open={open}
      onClose={handleClose}
      setOpen={setOpen}
      submitForm={handleUpload}
      caption="Аватар"
      buttonsSize={'l'}
      textButtonApply="Загрузить"
      textButtonCancel="Отмена"
      loading={isLoading}>
      <div className={styles.modalContent}>
        {isLoading && (
          <div className={styles.loader}>
            <Loader />
            <Text variant="body-1">Загружаем изображение</Text>
          </div>
        )}
        {previewUrl && !isPreparing && (
          <div className={styles.cropContainer}>
            <ReactCrop
              crop={crop || undefined}
              onChange={(_, percentCrop) => setCrop(percentCrop)}
              onComplete={(c: Crop) => setCompletedCrop(c)}
              aspect={1}
              circularCrop={true}
              className={styles.cropper}
              disabled={isLoading}>
              <img
                ref={imgRef}
                src={previewUrl}
                onLoad={onImageLoad}
                alt="Предпросмотр"
                className={styles.previewImage}
                style={{opacity: isLoading ? 0.5 : 1}}
              />
            </ReactCrop>
          </div>
        )}

        {/* Скрытый canvas для отрисовки обрезанного изображения */}
        <canvas ref={canvasRef} className={styles.hiddenCanvas} />
      </div>
    </ActionModal>
  );
};
